// The smart search on /features (Grant 2026-11-04: "including phrases people may
// search that we name differently"). Pure functions, so the ranking and the
// typo rule are pinned by tests and the page only draws.
//
// Every row of utils/features carries `keywords`: the words people use for
// things we name differently ("fixtures" for matches, "subs" for memberships,
// "court booking" for bookings). They are for matching only and are never
// shown as our own wording: the page highlights the row's own title and blurb.
//
// Matching: case-insensitive, accents dropped, simple plurals and stems
// (boxes/box, bookings/booking, scheduling/schedul), a prefix while someone is
// still typing, and one typo on words of five letters or more (edit distance
// 1). Every word of the query has to match somewhere on the row. Ranking: an
// exact title hit first, then a keyword hit, then the body (blurb, detail,
// the section's name), with the score as the tiebreak and the page's own
// order after that.
import {FEATURE_SECTIONS} from '~/utils/features';
import {formatExplainers} from '~/utils/formatExplainers';

export const TIER = Object.freeze({title: 1, keyword: 2, body: 3});
const TIER_SCORE = {[TIER.title]: 100, [TIER.keyword]: 60, [TIER.body]: 20};

/** Lower case, no accents, letters and digits only. */
export function normalise(text) {
  return String(text ?? '')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
}

/** A simple English stem: plurals and the common endings, enough for "bookings" to find "booking" and "boxes" "box". */
export function stem(word) {
  let w = String(word ?? '');
  if (w.length <= 3) return w;
  if (w.endsWith('ies') && w.length > 4) return w.slice(0, -3) + 'y';
  if (w.endsWith('sses')) return w.slice(0, -2);
  if (/(ches|shes|xes|zes|ses)$/.test(w) && w.length > 4) return w.slice(0, -2);
  if (w.endsWith('ing') && w.length > 5) return w.slice(0, -3);
  if (w.endsWith('ed') && w.length > 4) return w.slice(0, -2);
  if (w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
}

export function tokens(text) {
  return normalise(text).split(' ').filter(Boolean);
}

/**
 * Optimal string alignment distance (Damerau: two swapped letters are ONE edit, the most common typo), capped at 2,
 * which is all the matcher needs to know.
 */
export function editDistance(a, b) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 1) return 2;
  const rows = [];
  for (let i = 0; i <= a.length; i++) {
    rows[i] = [i];
    for (let j = 1; j <= b.length; j++) {
      if (i === 0) { rows[0][j] = j; continue; }
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, rows[i - 2][j - 2] + 1);
      rows[i][j] = v;
    }
  }
  return Math.min(rows[a.length][b.length], 2);
}

/**
 * How well a query word matches an indexed word: 1 exact (after stemming), 0.8 a prefix of it (three letters or more,
 * still typing), 0.5 one typo on a word of five letters or more, 0 otherwise.
 */
export function wordMatch(queryWord, indexWord, {prefix = true} = {}) {
  const q = stem(queryWord);
  const w = stem(indexWord);
  if (!q || !w) return 0;
  if (q === w || queryWord === indexWord) return 1;
  // a prefix while still typing: two letters are enough on the word being typed ("bo" → bookings, box leagues)
  if (prefix && q.length >= 2 && (w.startsWith(q) || indexWord.startsWith(queryWord))) return q.length >= 3 ? 0.8 : 0.6;
  // one typo: against the stems and against the words as typed (a typo inside an ending survives stemming badly)
  if (queryWord.length >= 5 && (editDistance(q, w) <= 1 || editDistance(queryWord, indexWord) <= 1 || editDistance(queryWord, w) <= 1 || editDistance(q, indexWord) <= 1)) return 0.5;
  return 0;
}

/** The searchable index of one row: its words by tier, with the original words kept for highlighting. */
export function indexItem(item, section) {
  const words = (text) => tokens(text);
  const keywords = (item.keywords ?? []).flatMap((k) => words(k));
  const phrases = (item.keywords ?? []).map((k) => normalise(k)).filter(Boolean);
  const body = [...words(item.blurb), ...(item.detail ?? []).flatMap((d) => words(d)), ...words(section?.title), ...words(section?.short)];
  return {
    key: item.key,
    title: words(item.title),
    keywords,
    phrases,
    body,
    // the row's text as one string, for a phrase typed with a hyphen or in quotes ("no-show", "pay and play")
    text: ` ${[normalise(item.title), normalise(item.blurb), ...(item.detail ?? []).map((d) => normalise(d))].join(' ')} `,
  };
}

const FEATURE_INDEX = FEATURE_SECTIONS.flatMap((section) => section.items.map((item) => ({item, section, index: indexItem(item, section)})));

/**
 * One row against a query: null when a query word matches nothing on it, else {tier, score, words} where `words` are
 * the row's own matched words (title and blurb) for the highlighter.
 */
export function matchItem(index, queryWords, queryPhrase = '', {phraseOnly = false} = {}) {
  let score = 0;
  let tier = null;
  const hits = new Set();
  const wholeWords = (text, phrase) => text === phrase || text.startsWith(phrase + ' ') || text.endsWith(' ' + phrase) || text.includes(' ' + phrase + ' ');
  // the whole query as one keyword phrase ("reserve a court", "club night"): every word is covered, and it ranks with
  // an exact title hit, above a title that merely starts with a word ("club" in "Clubhouse TV")
  const keywordPhrase = queryPhrase && queryPhrase.length >= 4 && queryWords.length > 1 && index.phrases.some((p) => wholeWords(p, queryPhrase));
  // a hyphenated or quoted query is a phrase: the row's own text (or a keyword phrase) has to carry it whole
  if (phraseOnly) {
    if (!queryPhrase) return null;
    if (keywordPhrase) return {tier: TIER.title, score: 90, words: []};
    if (wholeWords(index.text.trim(), queryPhrase) || index.text.includes(' ' + queryPhrase + ' ')) return {tier: TIER.body, score: TIER_SCORE[TIER.body] * queryWords.length, words: queryWords};
    return null;
  }
  queryWords.forEach((qw, i) => {
    if (tier === false) return;
    // a prefix only on the last word, the one still being typed
    const prefix = i === queryWords.length - 1;
    let best = 0;
    let bestTier = null;
    const scan = (list, t) => {
      for (const w of list) {
        const m = wordMatch(qw, w, {prefix});
        if (m > 0) {
          hits.add(w);
          const s = m * TIER_SCORE[t];
          // a title that only starts with the word ("club" → Clubhouse TV) is still a title hit, a little behind the exact one
          if (s > best) { best = s; bestTier = t; }
        }
      }
    };
    scan(index.title, TIER.title);
    scan(index.keywords, TIER.keyword);
    scan(index.body, TIER.body);
    if (!best && keywordPhrase) { best = TIER_SCORE[TIER.keyword] * 0.9; bestTier = TIER.keyword; }
    if (!best) { tier = false; return; }
    score += best;
    if (tier === null || bestTier < tier) tier = bestTier;
  });
  if (tier === false) return null;
  if (keywordPhrase) { score += 60; tier = TIER.title; }
  return {tier, score, words: [...hits]};
}

/**
 * The ranked matches for a query: [{item, section, tier, score, words}], exact title hits first, then keyword hits,
 * then body hits; a higher score first inside a tier; the page's own order after that. An empty query matches nothing.
 */
export function searchFeatures(query, entries = FEATURE_INDEX) {
  const words = tokens(query);
  if (!words.length) return [];
  const phrase = normalise(query);
  const phraseOnly = /["“”]|\w-\w/.test(String(query ?? ''));
  const out = [];
  entries.forEach((entry, order) => {
    const m = matchItem(entry.index, words, phrase, {phraseOnly});
    if (m) out.push({item: entry.item, section: entry.section, order, ...m});
  });
  return out.sort((a, b) => a.tier - b.tier || b.score - a.score || a.order - b.order);
}

/** The matched rows grouped back into their sections, sections in the order of their best match. */
export function searchSections(query, entries = FEATURE_INDEX) {
  const ranked = searchFeatures(query, entries);
  const groups = [];
  for (const r of ranked) {
    let g = groups.find((x) => x.key === r.section.key);
    if (!g) { g = {...r.section, items: [], words: new Set()}; groups.push(g); }
    g.items.push(r.item);
    r.words.forEach((w) => g.words.add(w));
  }
  return {ranked, groups: groups.map((g) => ({...g, words: [...g.words]}))};
}

/** The words to light on a row: the query's words plus the row's own matched words, as a safe regex source. */
export function highlightWords(query, matchedWords = []) {
  const list = [...new Set([...tokens(query), ...matchedWords].map((w) => stem(w)).filter((w) => w.length >= 2))];
  return list;
}

/** `text` cut into [{text, hit}] segments, a hit where a word starts with one of the (stemmed) words. */
export function highlightSegments(text, words) {
  const source = String(text ?? '');
  if (!source || !words?.length) return [{text: source, hit: false}];
  const escaped = [...new Set(words.map((w) => stem(normalise(w))))].map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).filter((w) => w.length >= 2);
  if (!escaped.length) return [{text: source, hit: false}];
  const re = new RegExp(`\\b(${escaped.join('|')})[a-z]*`, 'gi');
  const out = [];
  let last = 0;
  for (const m of source.matchAll(re)) {
    if (m.index > last) out.push({text: source.slice(last, m.index), hit: false});
    out.push({text: m[0], hit: true});
    last = m.index + m[0].length;
  }
  if (last < source.length) out.push({text: source.slice(last), hit: false});
  return out;
}

/** The query in the address: `?q=` trimmed, at most 80 characters. */
export function queryFromRoute(query) {
  return String(query?.q ?? '').slice(0, 80).trim();
}

/** The address for a query: `q` set, or left out when empty (router.replace, never push). */
export function routeQueryWith(query, q) {
  const next = {...query};
  const value = String(q ?? '').trim();
  if (value) next.q = value;
  else delete next.q;
  return next;
}

/** "3 matches for 'subs'", "1 match", "No matches for 'xyz'". */
export function matchesLine(count, q) {
  if (!count) return `No matches for "${q}"`;
  return `${count} match${count === 1 ? '' : 'es'} for "${q}"`;
}

// ── The autocomplete (Grant 2026-11-04: "it should be an autocomplete and be full width under the hero") ──

/** The sport pages, as the autocomplete offers them (the pages under app/pages/sports). */
export const SPORT_PAGES = Object.freeze([
  {key: 'sport-squash', title: 'Squash', to: '/sports/squash', icon: 'mdi-racquetball', blurb: 'Box leagues for squash clubs, with points-per-game handicaps.', keywords: ['squash club', 'squash box league', 'racquet']},
  {key: 'sport-tennis', title: 'Tennis', to: '/sports/tennis', icon: 'mdi-tennis', blurb: 'Ladders, box leagues and tournaments for tennis clubs.', keywords: ['tennis club', 'tennis ladder', 'LTA', 'WTN']},
  {key: 'sport-padel', title: 'Padel', to: '/sports/padel', icon: 'mdi-tennis-ball', blurb: 'Americano leagues and ladders for padel.', keywords: ['padel club', 'americano', 'mexicano']},
  {key: 'sport-pickleball', title: 'Pickleball', to: '/sports/pickleball', icon: 'mdi-tennis-ball', blurb: 'Ladders and round robins for pickleball.', keywords: ['pickleball club', 'DUPR']},
  {key: 'sport-badminton', title: 'Badminton', to: '/sports/badminton', icon: 'mdi-badminton', blurb: 'Leagues and ladders for badminton clubs.', keywords: ['badminton club', 'shuttle']},
  {key: 'sport-table-tennis', title: 'Table tennis', to: '/sports/table-tennis', icon: 'mdi-table-tennis', blurb: 'Leagues and ladders for table tennis.', keywords: ['ping pong', 'table tennis club']},
  {key: 'sport-racketball', title: 'Racketball', to: '/sports/racketball', icon: 'mdi-racquetball', blurb: 'Box leagues for racketball.', keywords: ['racquetball', 'squash 57']},
  {key: 'sport-darts', title: 'Darts', to: '/sports/darts', icon: 'mdi-bullseye-arrow', blurb: 'Leagues with 180s, checkouts and X01 scoring.', keywords: ['darts league', 'pub darts', '501', 'oche']},
  {key: 'sport-snooker', title: 'Snooker', to: '/sports/snooker', icon: 'mdi-billiards', blurb: 'Leagues and ladders scored in frames.', keywords: ['snooker club', 'frames']},
  {key: 'sport-pool', title: 'Pool', to: '/sports/pool', icon: 'mdi-billiards-rack', blurb: 'Pub and club pool leagues.', keywords: ['pool league', 'pub pool', '8 ball', 'eight ball']},
  {key: 'sport-bowls', title: 'Bowls', to: '/sports/bowls', icon: 'mdi-bowling', blurb: 'Leagues and handicaps in shots for bowls clubs.', keywords: ['lawn bowls', 'crown green', 'bowling club', 'rink']},
  {key: 'sport-football', title: 'Football', to: '/sports/football', icon: 'mdi-soccer', blurb: 'Weekly games with subs, RSVP and goals.', keywords: ['5 a side', 'five a side', 'soccer', 'pitch', 'kickabout']},
  {key: 'sport-basketball', title: 'Basketball', to: '/sports/basketball', icon: 'mdi-basketball', blurb: 'Weekly pickup games with RSVP.', keywords: ['pickup', 'hoops', 'basketball league']},
  {key: 'sport-foosball', title: 'Foosball', to: '/sports/foosball', icon: 'mdi-soccer-field', blurb: 'Office and bar leagues for table football.', keywords: ['table football', 'office league']},
  {key: 'sport-fencing', title: 'Fencing', to: '/sports/fencing', icon: 'mdi-fencing', blurb: 'Pools and direct elimination for fencing clubs.', keywords: ['piste', 'epee', 'foil', 'sabre']},
  {key: 'sport-board-game', title: 'Board games', to: '/sports/board-game', icon: 'mdi-dice-multiple', blurb: 'Leagues and ladders for chess, Scrabble and any board game.', keywords: ['chess', 'scrabble', 'backgammon', 'board game club']},
]);

/** The format pages, from the explainers (name, path, tagline, icon). */
export const FORMAT_PAGES = Object.freeze(Object.values(formatExplainers).map((f) => ({
  key: `format-${f.key}`, title: f.name, to: f.path, icon: f.icon, blurb: f.tagline, keywords: [],
})));

const FEATURE_PAGE_TITLES = {memberships: 'Memberships and passes', bookings: 'Playing area bookings', 'setup-assistant': 'Setup assistant and Tappy', 'clubhouse-tv': 'Clubhouse TV'};

/**
 * Everything the autocomplete can offer: the feature pages and the format pages (from the rows that have a page of
 * their own), the sport pages, then every other row. Each entry: {kind: 'page' | 'row', key, title, blurb, icon, to,
 * section, index}.
 */
export const SUGGEST_INDEX = (() => {
  const out = [];
  const seenPages = new Set();
  for (const section of FEATURE_SECTIONS) {
    for (const item of section.items) {
      const page = !!item.to && !seenPages.has(item.to);
      if (page) seenPages.add(item.to);
      const title = page && FEATURE_PAGE_TITLES[item.key] ? FEATURE_PAGE_TITLES[item.key] : item.title;
      out.push({kind: page ? 'page' : 'row', key: item.key, title, blurb: item.blurb, icon: item.icon, to: page ? item.to : null, section, index: indexItem({...item, title}, section)});
    }
  }
  for (const p of SPORT_PAGES) out.push({kind: 'page', key: p.key, title: p.title, blurb: p.blurb, icon: p.icon, to: p.to, section: {key: 'sports', title: 'Sports', short: 'Sports', icon: 'mdi-medal-outline'}, index: indexItem({title: p.title, blurb: p.blurb, keywords: [...p.keywords, 'sport', 'sports'], detail: []}, {title: 'Sports', short: 'Sports'})});
  return out;
})();

/** The keyword (or keyword phrase) the query matched when it matched no word of the title or the blurb: the "matches 'subs'" hint. */
export function synonymHit(entry, queryWords, queryPhrase) {
  const own = new Set([...entry.index.title, ...tokens(entry.blurb)]);
  const ownHit = queryWords.some((qw) => [...own].some((w) => wordMatch(qw, w, {prefix: true}) > 0));
  if (ownHit) return null;
  const phrase = (entry.index.phrases ?? []).find((p) => p === queryPhrase || p.startsWith(queryPhrase + ' ') || p.endsWith(' ' + queryPhrase) || p.includes(' ' + queryPhrase + ' '));
  if (phrase) return phrase;
  for (const qw of queryWords) {
    const kw = entry.index.keywords.find((w) => wordMatch(qw, w, {prefix: true}) > 0);
    if (kw) return kw;
  }
  return null;
}

/**
 * The best suggestions for what was typed: pages first (feature pages, formats, sports), then rows, each ranked as
 * searchFeatures ranks (title, keyword, body; score; page order), cut to `limit`. Each: {kind, key, title, blurb, icon,
 * to, section, tier, score, words, via} where `via` is the synonym the query matched when nothing in the title or
 * blurb did.
 */
export function suggest(query, {limit = 8, entries = SUGGEST_INDEX} = {}) {
  const words = tokens(query);
  if (!words.length) return [];
  const phrase = normalise(query);
  const phraseOnly = /["“”]|\w-\w/.test(String(query ?? ''));
  const hits = [];
  entries.forEach((entry, order) => {
    const m = matchItem(entry.index, words, phrase, {phraseOnly});
    if (!m) return;
    hits.push({...entry, order, ...m, via: synonymHit(entry, words, phrase)});
  });
  // relevance first (title, keyword, body), a page before a row at the same relevance, then the score and the page's
  // own order; the panel then draws the pages it kept as a group above the rows
  const kindRank = (h) => (h.kind === 'page' ? 0 : 1);
  hits.sort((a, b) => a.tier - b.tier || kindRank(a) - kindRank(b) || b.score - a.score || a.order - b.order);
  return hits.slice(0, limit).map(({index, ...h}) => h);
}
